/**
 * Implementation extended from:
 * https://www.contentful.com/developers/docs/tutorials/general/getting-started-with-rich-text-field-type/
 * https://github.com/contentful/rich-text/tree/master/packages/rich-text-react-renderer
 */

/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import type { Block, Document, Hyperlink, Text } from '@contentful/rich-text-types';

import {
  AccordionHeader,
  AccordionPanel,
  CardMetadata,
  Grid,
  Link,
  ProductCard,
  Typography2,
  breakpoints,
  typography2,
  useMediaQuery,
} from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import { isExternalLink } from 'bundles/common/utils/urlUtils';
import type {
  UniversityProgramDegreeDescriptionPageAdmissionsProcessLinks as AdmissionsProcessLinks,
  Faq,
  GeneralProductCard,
} from 'bundles/degree-description/__generated__/baseContentfulTypes';
import FaqModuleAccordionItem from 'bundles/degree-description/components/modules/faqs/FaqModuleAccordionItem';
import Container from 'bundles/degree-description/components/shared/Container';
import { useDegreeDescriptionPageData } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import type { DegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

const richTextEmbedTypes = {
  faq: 'Faq',
  productCard: 'GeneralProductCard',
} as const;

const styles = {
  description: (theme: DegreeDescriptionPageTheme) => css`
    margin: var(--cds-spacing-600) 0;

    & > h3 {
      ${theme.typography.h1semibold};
      ${theme.mobileFontSizeStyle.header};
      padding-bottom: var(--cds-spacing-200);
      padding-top: var(--cds-spacing-200);
    }

    & > h4 {
      ${theme.typography.h2semibold}
      ${theme.mobileFontSizeStyle.subHeader};
      padding-bottom: var(--cds-spacing-200);
      padding-top: var(--cds-spacing-200);
    }

    & > p {
      ${typography2.bodyPrimary};
      padding-top: 16px;
      ${theme.mobileFontSizeStyle?.body};
    }

    .rc-Markdown.styled {
      h2,
      h3,
      h4,
      h5,
      h6,
      p,
      li {
        font-family: ${theme.typography.fontFamily};
      }

      p {
        ${typography2.bodyPrimary};
        padding-top: 16px;
        ${theme.mobileFontSizeStyle?.body};
      }

      a {
        color: var(--cds-color-blue-700);

        &:hover,
        &:focus {
          text-decoration: none;
        }

        &:visited {
          color: var(--cds-color-purple-950);
        }
      }
    }
  `,
  productGrid: css`
    display: inline-flex;
    padding-top: var(--cds-spacing-300);
    width: 100%;
    ${breakpoints.up('sm')} {
      height: 460px;
      padding-right: var(--cds-spacing-300);
    }
  `,
};

type ProductCardOnClick = (linkURL?: string | null, linkName?: string | null) => () => void;

type Props = {
  content: Document;
  links?: AdmissionsProcessLinks;
  getRichTextOptions?: (
    links?: AdmissionsProcessLinks,
    isMobile?: boolean,
    productCardOnClick?: ProductCardOnClick,
    trackingProductId?: string
  ) => Options;
};

export const getDefaultRichTextOptions = (
  links?: AdmissionsProcessLinks,
  isMobile?: boolean,
  productCardOnClick?: ProductCardOnClick,
  trackingProductId?: string
): Options => {
  let faqCounter = 0;

  return {
    renderNode: {
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const { trackClickButton } = useDegreeDescriptionEventingContext();

        const blockNode = node as Block;
        const id = blockNode?.data?.target?.sys?.id;
        const entry = links?.entries?.block?.find((link) => link?.sys?.id === id) as Faq | GeneralProductCard;

        const handleOnClick = () => {
          const linkURL = 'productUrl' in entry && entry?.productUrl ? entry.productUrl : null;
          trackClickButton('degree_info_link', 'admission_info', linkURL);
        };

        const isFirstFAQ = faqCounter === 0;

        switch (entry?.__typename) {
          case richTextEmbedTypes.faq:
            if (!entry.question || !entry.answer) {
              return null;
            }
            faqCounter += 1;
            return (
              <FaqModuleAccordionItem
                accordionVariant="standard"
                trackingName="admissions_faq"
                eventingV3TrackingData={{
                  name: 'admission_item',
                  header: entry.question,
                  body: entry.answer ?? '',
                }}
                trackingSection="application_requirements"
                defaultExpanded={isFirstFAQ}
                withPadding
              >
                <AccordionHeader label={entry.question} />
                <AccordionPanel>
                  <Markdown
                    source={entry.answer}
                    trackingName="admissions_faq_link"
                    buttonTrackingNameV3="degree_info_link"
                    trackingSection="application_requirements"
                    trackingProductId={trackingProductId}
                    onLinkClick={handleOnClick}
                  />
                </AccordionPanel>
              </FaqModuleAccordionItem>
            );
          case richTextEmbedTypes.productCard:
            return (
              <Grid xs={12} sm={6} md={4} lg={3} css={styles.productGrid}>
                <ProductCard
                  id={entry.sys.id}
                  variant="grid"
                  productType={entry.productType ?? ''}
                  title={{
                    name: entry?.title ?? '',
                    linkProps: {
                      href: entry.productUrl ?? '',
                      component: TrackedLink2,
                      ...(productCardOnClick && {
                        onClick: productCardOnClick(entry.productUrl, entry?.title),
                      }),
                    },
                    customLinkProps: {
                      trackingName: 'admissions_card',
                      withVisibilityTracking: true,
                    },
                  }}
                  body={
                    <Typography2 variant="bodySecondary" color="supportText" component="h4">
                      {entry.description}
                    </Typography2>
                  }
                  {...(entry.productLengthOverrideText && {
                    footer: <CardMetadata metadata={[entry.productLengthOverrideText]} />,
                  })}
                  {...(!isMobile && { previewImageSrc: entry.productImage?.url || '' })}
                />
              </Grid>
            );
          default:
            return null;
        }
      },

      [INLINES.HYPERLINK]: (node) => {
        const inlineNode = node as Hyperlink;
        const isBold = inlineNode?.content[0]?.marks[0]?.type === 'bold';
        return (
          <Link
            component={TrackedLink2}
            href={node.data.uri}
            target={isExternalLink(node.data.uri) ? '_blank' : undefined}
            rel={isExternalLink(node.data.uri) ? 'noopener noreferrer' : undefined}
            trackingName="ddp_app_process_link"
            css={css`
              font-weight: ${isBold ? 'bold' : 'inherit'};
              text-decoration: underline;
            `}
          >
            {(node.content[0] as Text).value}
          </Link>
        );
      },
    },
  };
};

const AdmissionsProcessModule = ({ content, links, getRichTextOptions = getDefaultRichTextOptions }: Props) => {
  const { trackClickButton } = useDegreeDescriptionEventingContext();
  const theme = useDegreeDescriptionPageTheme();
  const isMobile = useMediaQuery(breakpoints.down('xs'));
  const { id: degreeId } = useDegreeDescriptionPageData();
  const trackingProductId = degreeId.replace(/^base~/, '');

  const productCardOnClick: ProductCardOnClick = (linkURL, linkName) => () => {
    trackClickButton('degree_info_link', 'page_body', linkURL, linkName);
  };

  return (
    <div className="rc-RichText rc-AdmissionsProcessModule">
      <Container>
        <div css={styles.description(theme)}>
          {documentToReactComponents(
            content,
            getRichTextOptions(links, isMobile, productCardOnClick, trackingProductId)
          )}
        </div>
      </Container>
    </div>
  );
};

export default AdmissionsProcessModule;
